import React, { useEffect } from "react";
import { useCookies } from "react-cookie";
import {
  AUTH_TOKEN,
  PERMISSIONS_KEY,
  TOKEN,
  User_Status,
} from "../../../shared/constants/AppConst";
import { useDispatch, useSelector } from "react-redux";
import {
  setAuthenticated,
  setInitialUrl,
  setIsMyProfileLoading,
  setMyPermissions,
  setMyProfile,
} from "src/domain/app/redux/auth/auth-slice";
import { getInitialUrl } from "src/domain/app/redux/auth/auth-selectors";
import MainUtils from "src/shared/utils/main";
import routesConfig from "src/pages/routeConfig";
import { rolesManagementService } from "src/shared/services/roles-management";
import { usersManagementService } from "src/shared/services/users-management";
import { setCountNotificationsUnread } from "src/domain/app/redux/notifications/notification-slice";
import { signalRHub } from "src/shared/services/chat/signalr-hub";
import { store } from "src/domain/app/redux/store";
import {
  setupGlobalChatListeners,
  loadChatRooms,
} from "src/shared/services/chat/realtime";

const AuthProvider = (props: { children: React.ReactNode }) => {
  const [cookies, , removeCookie] = useCookies([AUTH_TOKEN]);
  const dispatch = useDispatch();
  const pathName = useSelector(getInitialUrl);
  useEffect(() => {
    if (cookies[AUTH_TOKEN]) {
      dispatch(setAuthenticated(true));
      (async () => {
        try {
          dispatch(setIsMyProfileLoading(true));
          await usersManagementService.getMyProfile().then(async (response) => {
            const data = response.Data;
            if (data) {
              dispatch(setMyProfile(data));
              dispatch(
                setCountNotificationsUnread(data?.notificationsCount ?? 0),
              );

              // Listeners first: startConnection replays them on (re)connect
              setupGlobalChatListeners();
              // Load chat rooms so unread badge is accurate before the popup is opened
              loadChatRooms();
              try {
                await signalRHub.startConnection();
              } catch (error) {
                console.error("Failed to initialize SignalR:", error);
              }
            }
          });
          dispatch(setIsMyProfileLoading(false));

          await rolesManagementService.getMyRoles().then((response) => {
            if (!MainUtils.isEmptyValue(response?.Data)) {
              let permissionsAll = response?.Data?.rolePermissions
                .map((permission) => {
                  return permission.name;
                })
                .reduce((acc: any, curr: any) => acc.concat(curr), []);
              dispatch(setMyPermissions(permissionsAll));
              localStorage.setItem(
                PERMISSIONS_KEY,
                JSON.stringify(permissionsAll),
              );
              let myPermissionsIsSidebar: any[] = [];
              routesConfig
                .filter(
                  (item: any) => {
                    const userType = store.getState().auth?.myProfile?.userType;
                    const permOk = permissionsAll.filter((permission: any) =>
                      item.permissionKey.includes(permission),
                    )?.length > 0;
                    const typeOk = item.userTypeKey == null || item.userTypeKey === userType;
                    return permOk && typeOk;
                  },
                )
                .map((item: any) => {
                  if (item.children) {
                    const children = item.children.filter((child: any) =>
                      permissionsAll.includes(child.permissionKey),
                    );
                    if (children?.length > 0)
                      myPermissionsIsSidebar.push(...children);
                    return;
                  }
                  myPermissionsIsSidebar.push(item);
                  return;
                });
              if (
                myPermissionsIsSidebar &&
                myPermissionsIsSidebar?.length > 0
              ) {
                dispatch(
                  setInitialUrl(
                    myPermissionsIsSidebar.filter((item: any) => item.path)[0]
                      .path,
                  ),
                );
              } else dispatch(setInitialUrl("/error-pages/error-403"));
            }
          });
        } catch (error) {
          console.error(error);
        } finally {
          dispatch(setIsMyProfileLoading(false));
        }
      })();
    } else {
      dispatch(setAuthenticated(false));
      removeCookie(TOKEN);
      removeCookie(User_Status);
      dispatch(setInitialUrl("/sign-in"));

      // Cleanup SignalR connection on logout
      // The global chat listeners live on the hub client and are re-attached to
      // the new connection on the next sign-in, so they are not torn down here.
      signalRHub.stopConnection().catch((error) => {
        console.error("Failed to stop SignalR connection:", error);
      });
    }
  }, [cookies[AUTH_TOKEN], routesConfig]);

  const Loader = () => (
    <div className="loader">
      <div className="site-logo">
        <img src="/assets/images/logo.svg" alt="logo" />
      </div>
      <div className="loader-spin">
        <span className="ant-spin-dot ant-spin-dot-spin">
          <i></i>
          <i></i>
          <i></i>
          <i></i>
        </span>
      </div>
    </div>
  );
  return <>{pathName !== "/" ? props.children : <Loader />}</>;
};

export default AuthProvider;
